import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  Divider,
  Button,
  Typography
} from '@mui/material';

const AdvancedFilterDialog = ({
  open,
  onClose,
  adv,
  setAdv,
  departments,
  designations,
  employees,
  eduLevels,
  expRanges,
  skillsMaster = [],
  certsMaster = [],
  langsMaster = []
}) => {

  const selectProps = {
    displayEmpty: true,
    disablePortal: true,
    MenuProps: {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
      },
      sx: {
        zIndex: 9999,
      }
    }
  };

  const fieldSx = {
    '& .MuiInputBase-root': {
      height: 38,
      fontSize: '13px',
    },
    '& .MuiInputBase-input': {
      fontSize: '13px',
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
    }
  };

  const sectionTitleSx = {
    fontWeight: 'bold',
    fontSize: '13px',
    color: '#3f4f63',
    whiteSpace: 'nowrap'
  };

  const rowSx = {
    mb: 2
  };

  const clearAdvancedFilter = () => {
    setAdv({
      keyword: '',
      department_id: '',
      designation_id: '',
      emp_level: '',
      leave_approver_l1: '',
      leave_approver_l2: '',
      doj_from: '',
      doj_to: '',
      dob_from: '',
      dob_to: '',
      gender: '',
      blood_group: '',
      education_id: '',
      experience_id: '',
      skill: '',
      certification: '',
      language: ''
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          zIndex: 1300,
          borderRadius: '14px',
        }
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 'bold',
          bgcolor: '#f0f4ff',
          color: 'primary.main',
          fontSize: '18px',
          py: 2
        }}
      >
        Advanced Search & Filter
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>

        {/* Line 1 - Keyword Search */}
        <Grid container spacing={2} alignItems="center" sx={rowSx}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              placeholder="Keyword Search (Email, Mobile, Address, Skills, Certs, PAN, etc.)"
              value={adv.keyword}
              onChange={e => setAdv({ ...adv, keyword: e.target.value })}
              sx={fieldSx}
            />
          </Grid>
        </Grid>

        {/* Line 2 - Organization */}
        <Grid container spacing={2} alignItems="center" sx={rowSx}>
          <Grid item xs={12} md={2}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Organization
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.department_id}
              onChange={e => setAdv({ ...adv, department_id: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>Department</em>
              </MenuItem>
              {departments.map(d => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.designation_id}
              onChange={e => setAdv({ ...adv, designation_id: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>Designation</em>
              </MenuItem>
              {designations.map(d => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.emp_level}
              onChange={e => setAdv({ ...adv, emp_level: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>Employee Level</em>
              </MenuItem>
              <MenuItem value="Level 1">Level 1</MenuItem>
              <MenuItem value="Level 2">Level 2</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.leave_approver_l1}
              onChange={e => setAdv({ ...adv, leave_approver_l1: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>L1 Manager</em>
              </MenuItem>
              {employees.map(e => (
                <MenuItem key={e.emp_id} value={e.emp_id}>
                  {e.first_name} {e.last_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.leave_approver_l2}
              onChange={e => setAdv({ ...adv, leave_approver_l2: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>L2 Manager</em>
              </MenuItem>
              {employees.map(e => (
                <MenuItem key={e.emp_id} value={e.emp_id}>
                  {e.first_name} {e.last_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* Line 3 - Date Ranges */}
        <Grid container spacing={2} alignItems="center" sx={rowSx}>
          <Grid item xs={12} md={2}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Date Ranges
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="DOJ (From)"
              InputLabelProps={{ shrink: true }}
              value={adv.doj_from}
              onChange={e => setAdv({ ...adv, doj_from: e.target.value })}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="DOJ (To)"
              InputLabelProps={{ shrink: true }}
              value={adv.doj_to}
              onChange={e => setAdv({ ...adv, doj_to: e.target.value })}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="DOB (From)"
              InputLabelProps={{ shrink: true }}
              value={adv.dob_from}
              onChange={e => setAdv({ ...adv, dob_from: e.target.value })}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="DOB (To)"
              InputLabelProps={{ shrink: true }}
              value={adv.dob_to}
              onChange={e => setAdv({ ...adv, dob_to: e.target.value })}
              sx={fieldSx}
            />
          </Grid>
        </Grid>

        {/* Line 4 - Personal & Education */}
        <Grid container spacing={2} alignItems="center" sx={rowSx}>
          <Grid item xs={12} md={2}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Personal & Education
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.gender}
              onChange={e => setAdv({ ...adv, gender: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>Gender</em>
              </MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.blood_group}
              onChange={e => setAdv({ ...adv, blood_group: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>Blood Group</em>
              </MenuItem>
              <MenuItem value="A+">A+</MenuItem>
              <MenuItem value="A-">A-</MenuItem>
              <MenuItem value="O+">O+</MenuItem>
              <MenuItem value="O-">O-</MenuItem>
              <MenuItem value="B+">B+</MenuItem>
              <MenuItem value="AB+">AB+</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.education_id}
              onChange={e => setAdv({ ...adv, education_id: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>Education</em>
              </MenuItem>
              {eduLevels.map(e => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.experience_id}
              onChange={e => setAdv({ ...adv, experience_id: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>Experience</em>
              </MenuItem>
              {expRanges.map(e => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* Line 5 - Skills, Certs & Languages */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>
              Skills & Qualifications
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12} md={3.3}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.skill || ''}
              onChange={e => setAdv({ ...adv, skill: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>Select Skill</em>
              </MenuItem>
              {skillsMaster.map(s => (
                <MenuItem key={s.name} value={s.name}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3.3}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.certification || ''}
              onChange={e => setAdv({ ...adv, certification: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>Select Certification</em>
              </MenuItem>
              {certsMaster.map(c => (
                <MenuItem key={c.name} value={c.name}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={3.3}>
            <TextField
              select
              fullWidth
              size="small"
              value={adv.language || ''}
              onChange={e => setAdv({ ...adv, language: e.target.value })}
              SelectProps={selectProps}
              sx={fieldSx}
            >
              <MenuItem value="">
                <em>Select Language</em>
              </MenuItem>
              {langsMaster.map(l => (
                <MenuItem key={l.name} value={l.name}>
                  {l.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#fbfbfb' }}>
        <Button
          onClick={clearAdvancedFilter}
          color="inherit"
        >
          Clear All
        </Button>

        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          disableElevation
        >
          Apply & Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdvancedFilterDialog;